import React, { useMemo, useState } from 'react';
import type { FormInstance } from 'antd';
import { Button, Card, Collapse, ColorPicker, Form, Input, InputNumber, Select, Space, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { asPreviewCss } from '../utils/bookDefaultTemplates';

type Props = {
  form: FormInstance;
  /**
   * If provided, allows parent to override the text above preview.
   */
  previewTitle?: string;
};

export const BookDefaultStylesEditor: React.FC<Props> = ({ form, previewTitle }) => {
  const [advancedVisible, setAdvancedVisible] = useState(false);
  const [advancedKeys, setAdvancedKeys] = useState<string[]>(['headerAdvanced']);

  const headerTextColor = Form.useWatch(['defaultHeaderStyle', 'color'], form);
  const headerBgColor = Form.useWatch(['defaultHeaderStyle', 'backgroundColor'], form);
  const headerBorderColor = Form.useWatch(['defaultHeaderStyle', 'borderColor'], form);

  const lineTextColor = Form.useWatch(['defaultLineStyle', 'color'], form);
  const lineBgColor = Form.useWatch(['defaultLineStyle', 'backgroundColor'], form);
  const lineBorderColor = Form.useWatch(['defaultLineStyle', 'borderColor'], form);

  const nestedLineTextColor = Form.useWatch(['defaultNestedLineStyle', 'color'], form);
  const nestedLineBgColor = Form.useWatch(['defaultNestedLineStyle', 'backgroundColor'], form);
  const nestedLineBorderColor = Form.useWatch(['defaultNestedLineStyle', 'borderColor'], form);

  const deepNestedLineTextColor = Form.useWatch(['defaultDeepNestedLineStyle', 'color'], form);
  const deepNestedLineBgColor = Form.useWatch(['defaultDeepNestedLineStyle', 'backgroundColor'], form);
  const deepNestedLineBorderColor = Form.useWatch(['defaultDeepNestedLineStyle', 'borderColor'], form);

  const linkTextColor = Form.useWatch(['defaultLinkStyle', 'color'], form);
  const linkBgColor = Form.useWatch(['defaultLinkStyle', 'backgroundColor'], form);
  const linkBorderColor = Form.useWatch(['defaultLinkStyle', 'borderColor'], form);

  const openSection = (keys: string[]) => {
    setAdvancedVisible(true);
    setAdvancedKeys(keys);
  };

  const collapseItems = useMemo(() => {
    return [
      {
        key: 'linkAdvanced',
        label: 'Links (advanced style)',
        children: (
          <Collapse
            size="small"
            defaultActiveKey={['colors']}
            items={[
              {
                key: 'colors',
                label: 'Colors',
                children: (
                  <>
                    <Form.Item label="Text color">
                      <Space wrap>
                        <Form.Item name={['defaultLinkStyle', 'color']} noStyle>
                          <Input placeholder="#1890ff" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={(typeof linkTextColor === 'string' && linkTextColor.trim() ? linkTextColor : undefined) || '#1890ff'}
                          onChange={(c) => form.setFieldValue(['defaultLinkStyle', 'color'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                    <Form.Item label="Background color">
                      <Space wrap>
                        <Form.Item name={['defaultLinkStyle', 'backgroundColor']} noStyle>
                          <Input placeholder="#ffffff" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={(typeof linkBgColor === 'string' && linkBgColor.trim() ? linkBgColor : undefined) || '#ffffff'}
                          onChange={(c) => form.setFieldValue(['defaultLinkStyle', 'backgroundColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'border',
                label: 'Border',
                children: (
                  <Space wrap>
                    <Form.Item name={['defaultLinkStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                      <InputNumber min={0} max={12} style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name={['defaultLinkStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                      <Select
                        style={{ width: 160 }}
                        options={[
                          { label: 'None', value: 'none' },
                          { label: 'Solid', value: 'solid' },
                          { label: 'Dashed', value: 'dashed' },
                          { label: 'Dotted', value: 'dotted' },
                          { label: 'Double', value: 'double' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                      <Space wrap>
                        <Form.Item name={['defaultLinkStyle', 'borderColor']} noStyle>
                          <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={(typeof linkBorderColor === 'string' && linkBorderColor.trim() ? linkBorderColor : undefined) || '#d9d9d9'}
                          onChange={(c) => form.setFieldValue(['defaultLinkStyle', 'borderColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                  </Space>
                ),
              },
            ]}
          />
        ),
      },
      {
        key: 'headerAdvanced',
        label: 'Header (advanced style)',
        children: (
          <Collapse
            size="small"
            defaultActiveKey={['colors', 'typography']}
            items={[
              {
                key: 'colors',
                label: 'Colors',
                children: (
                  <>
                    <Form.Item label="Text color">
                      <Space wrap>
                        <Form.Item name={['defaultHeaderStyle', 'color']} noStyle>
                          <Input placeholder="#111111" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof headerTextColor === 'string' && headerTextColor.trim() ? headerTextColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultHeaderStyle', 'color'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                    <Form.Item label="Background color">
                      <Space wrap>
                        <Form.Item name={['defaultHeaderStyle', 'backgroundColor']} noStyle>
                          <Input placeholder="#ffffff" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof headerBgColor === 'string' && headerBgColor.trim() ? headerBgColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultHeaderStyle', 'backgroundColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'typography',
                label: 'Typography',
                children: (
                  <>
                    <Form.Item name={['defaultHeaderStyle', 'fontSize']} label="Font size">
                      <InputNumber min={10} max={72} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name={['defaultHeaderStyle', 'fontFamily']} label="Font type (family)">
                      <Select
                        allowClear
                        placeholder="Default"
                        options={[
                          { label: 'Default', value: '' },
                          { label: 'Vag', value: 'Vag' },
                          { label: 'Vag-Bold', value: 'Vag-Bold' },
                          { label: 'Serif', value: 'serif' },
                          { label: 'Sans', value: 'sans-serif' },
                          { label: 'Monospace', value: 'monospace' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item name={['defaultHeaderStyle', 'fontWeight']} label="Font weight">
                      <Select
                        options={[
                          { label: 'Normal (400)', value: 400 },
                          { label: 'SemiBold (600)', value: 600 },
                          { label: 'Bold (700)', value: 700 },
                        ]}
                      />
                    </Form.Item>
                    <Space wrap style={{ marginBottom: 12 }}>
                      <Form.Item name={['defaultHeaderStyle', 'italic']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 140 }}
                          options={[
                            { label: 'Normal', value: false },
                            { label: 'Italic', value: true },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item name={['defaultHeaderStyle', 'underline']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 160 }}
                          options={[
                            { label: 'No underline', value: false },
                            { label: 'Underline', value: true },
                          ]}
                        />
                      </Form.Item>
                    </Space>
                  </>
                ),
              },
              {
                key: 'border',
                label: 'Border',
                children: (
                  <Space wrap>
                    <Form.Item name={['defaultHeaderStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                      <InputNumber min={0} max={12} style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name={['defaultHeaderStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                      <Select
                        style={{ width: 160 }}
                        options={[
                          { label: 'None', value: 'none' },
                          { label: 'Solid', value: 'solid' },
                          { label: 'Dashed', value: 'dashed' },
                          { label: 'Dotted', value: 'dotted' },
                          { label: 'Double', value: 'double' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                      <Space wrap>
                        <Form.Item name={['defaultHeaderStyle', 'borderColor']} noStyle>
                          <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof headerBorderColor === 'string' && headerBorderColor.trim() ? headerBorderColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultHeaderStyle', 'borderColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                    <Form.Item name={['defaultHeaderStyle', 'borderRadius']} label="Border radius" style={{ marginBottom: 0 }}>
                      <InputNumber min={0} max={24} style={{ width: 160 }} />
                    </Form.Item>
                  </Space>
                ),
              },
            ]}
          />
        ),
      },
      {
        key: 'lineAdvanced',
        label: 'Lines (advanced style)',
        children: (
          <Collapse
            size="small"
            defaultActiveKey={['colors', 'typography']}
            items={[
              {
                key: 'colors',
                label: 'Colors',
                children: (
                  <>
                    <Form.Item label="Text color">
                      <Space wrap>
                        <Form.Item name={['defaultLineStyle', 'color']} noStyle>
                          <Input placeholder="#111111" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof lineTextColor === 'string' && lineTextColor.trim() ? lineTextColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultLineStyle', 'color'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                    <Form.Item label="Background color">
                      <Space wrap>
                        <Form.Item name={['defaultLineStyle', 'backgroundColor']} noStyle>
                          <Input placeholder="#ffffff" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof lineBgColor === 'string' && lineBgColor.trim() ? lineBgColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultLineStyle', 'backgroundColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'typography',
                label: 'Typography',
                children: (
                  <>
                    <Form.Item name={['defaultLineStyle', 'fontSize']} label="Font size">
                      <InputNumber min={10} max={72} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name={['defaultLineStyle', 'fontFamily']} label="Font type (family)">
                      <Select
                        allowClear
                        placeholder="Default"
                        options={[
                          { label: 'Default', value: '' },
                          { label: 'Vag', value: 'Vag' },
                          { label: 'Vag-Bold', value: 'Vag-Bold' },
                          { label: 'Serif', value: 'serif' },
                          { label: 'Sans', value: 'sans-serif' },
                          { label: 'Monospace', value: 'monospace' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item name={['defaultLineStyle', 'span']} label="Grid span">
                      <Select
                        allowClear
                        placeholder="Default (full)"
                        options={[
                          { label: 'Full (24)', value: 24 },
                          { label: 'Half (12)', value: 12 },
                        ]}
                      />
                    </Form.Item>
                    <Space wrap style={{ marginBottom: 12 }}>
                      <Form.Item name={['defaultLineStyle', 'bold']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 140 }}
                          options={[
                            { label: 'Normal', value: false },
                            { label: 'Bold', value: true },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item name={['defaultLineStyle', 'italic']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 140 }}
                          options={[
                            { label: 'Normal', value: false },
                            { label: 'Italic', value: true },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item name={['defaultLineStyle', 'underline']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 160 }}
                          options={[
                            { label: 'No underline', value: false },
                            { label: 'Underline', value: true },
                          ]}
                        />
                      </Form.Item>
                    </Space>
                  </>
                ),
              },
              {
                key: 'border',
                label: 'Border',
                children: (
                  <Space wrap>
                    <Form.Item name={['defaultLineStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                      <InputNumber min={0} max={12} style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name={['defaultLineStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                      <Select
                        style={{ width: 160 }}
                        options={[
                          { label: 'None', value: 'none' },
                          { label: 'Solid', value: 'solid' },
                          { label: 'Dashed', value: 'dashed' },
                          { label: 'Dotted', value: 'dotted' },
                          { label: 'Double', value: 'double' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                      <Space wrap>
                        <Form.Item name={['defaultLineStyle', 'borderColor']} noStyle>
                          <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof lineBorderColor === 'string' && lineBorderColor.trim() ? lineBorderColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultLineStyle', 'borderColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                  </Space>
                ),
              },
            ]}
          />
        ),
      },
      {
        key: 'nestedLineAdvanced',
        label: 'Nested lines (level 1)',
        children: (
          <Collapse
            size="small"
            defaultActiveKey={['colors', 'typography']}
            items={[
              {
                key: 'colors',
                label: 'Colors',
                children: (
                  <>
                    <Form.Item label="Text color">
                      <Space wrap>
                        <Form.Item name={['defaultNestedLineStyle', 'color']} noStyle>
                          <Input placeholder="#111111" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof nestedLineTextColor === 'string' && nestedLineTextColor.trim() ? nestedLineTextColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultNestedLineStyle', 'color'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                    <Form.Item label="Background color">
                      <Space wrap>
                        <Form.Item name={['defaultNestedLineStyle', 'backgroundColor']} noStyle>
                          <Input placeholder="#ffffff" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof nestedLineBgColor === 'string' && nestedLineBgColor.trim() ? nestedLineBgColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultNestedLineStyle', 'backgroundColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'typography',
                label: 'Typography',
                children: (
                  <>
                    <Form.Item name={['defaultNestedLineStyle', 'fontSize']} label="Font size">
                      <InputNumber min={10} max={72} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name={['defaultNestedLineStyle', 'fontFamily']} label="Font type (family)">
                      <Select
                        allowClear
                        placeholder="Default"
                        options={[
                          { label: 'Default', value: '' },
                          { label: 'Vag', value: 'Vag' },
                          { label: 'Vag-Bold', value: 'Vag-Bold' },
                          { label: 'Serif', value: 'serif' },
                          { label: 'Sans', value: 'sans-serif' },
                          { label: 'Monospace', value: 'monospace' },
                        ]}
                      />
                    </Form.Item>
                    <Space wrap style={{ marginBottom: 12 }}>
                      <Form.Item name={['defaultNestedLineStyle', 'bold']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 140 }}
                          options={[
                            { label: 'Normal', value: false },
                            { label: 'Bold', value: true },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item name={['defaultNestedLineStyle', 'italic']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 140 }}
                          options={[
                            { label: 'Normal', value: false },
                            { label: 'Italic', value: true },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item name={['defaultNestedLineStyle', 'underline']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 160 }}
                          options={[
                            { label: 'No underline', value: false },
                            { label: 'Underline', value: true },
                          ]}
                        />
                      </Form.Item>
                    </Space>
                  </>
                ),
              },
              {
                key: 'border',
                label: 'Border',
                children: (
                  <Space wrap>
                    <Form.Item name={['defaultNestedLineStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                      <InputNumber min={0} max={12} style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name={['defaultNestedLineStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                      <Select
                        style={{ width: 160 }}
                        options={[
                          { label: 'None', value: 'none' },
                          { label: 'Solid', value: 'solid' },
                          { label: 'Dashed', value: 'dashed' },
                          { label: 'Dotted', value: 'dotted' },
                          { label: 'Double', value: 'double' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                      <Space wrap>
                        <Form.Item name={['defaultNestedLineStyle', 'borderColor']} noStyle>
                          <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof nestedLineBorderColor === 'string' && nestedLineBorderColor.trim() ? nestedLineBorderColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultNestedLineStyle', 'borderColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                  </Space>
                ),
              },
            ]}
          />
        ),
      },
      {
        key: 'deepNestedLineAdvanced',
        label: 'Nested lines (level 2+)',
        children: (
          <Collapse
            size="small"
            defaultActiveKey={['colors', 'typography']}
            items={[
              {
                key: 'colors',
                label: 'Colors',
                children: (
                  <>
                    <Form.Item label="Text color">
                      <Space wrap>
                        <Form.Item name={['defaultDeepNestedLineStyle', 'color']} noStyle>
                          <Input placeholder="#111111" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof deepNestedLineTextColor === 'string' && deepNestedLineTextColor.trim() ? deepNestedLineTextColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultDeepNestedLineStyle', 'color'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                    <Form.Item label="Background color">
                      <Space wrap>
                        <Form.Item name={['defaultDeepNestedLineStyle', 'backgroundColor']} noStyle>
                          <Input placeholder="#ffffff" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof deepNestedLineBgColor === 'string' && deepNestedLineBgColor.trim() ? deepNestedLineBgColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultDeepNestedLineStyle', 'backgroundColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'typography',
                label: 'Typography',
                children: (
                  <>
                    <Form.Item name={['defaultDeepNestedLineStyle', 'fontSize']} label="Font size">
                      <InputNumber min={10} max={72} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name={['defaultDeepNestedLineStyle', 'fontFamily']} label="Font type (family)">
                      <Select
                        allowClear
                        placeholder="Default"
                        options={[
                          { label: 'Default', value: '' },
                          { label: 'Vag', value: 'Vag' },
                          { label: 'Vag-Bold', value: 'Vag-Bold' },
                          { label: 'Serif', value: 'serif' },
                          { label: 'Sans', value: 'sans-serif' },
                          { label: 'Monospace', value: 'monospace' },
                        ]}
                      />
                    </Form.Item>
                    <Space wrap style={{ marginBottom: 12 }}>
                      <Form.Item name={['defaultDeepNestedLineStyle', 'bold']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 140 }}
                          options={[
                            { label: 'Normal', value: false },
                            { label: 'Bold', value: true },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item name={['defaultDeepNestedLineStyle', 'italic']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 140 }}
                          options={[
                            { label: 'Normal', value: false },
                            { label: 'Italic', value: true },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item name={['defaultDeepNestedLineStyle', 'underline']} style={{ marginBottom: 0 }}>
                        <Select
                          style={{ width: 160 }}
                          options={[
                            { label: 'No underline', value: false },
                            { label: 'Underline', value: true },
                          ]}
                        />
                      </Form.Item>
                    </Space>
                  </>
                ),
              },
              {
                key: 'border',
                label: 'Border',
                children: (
                  <Space wrap>
                    <Form.Item name={['defaultDeepNestedLineStyle', 'borderWidth']} label="Border width" style={{ marginBottom: 0 }}>
                      <InputNumber min={0} max={12} style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item name={['defaultDeepNestedLineStyle', 'borderStyle']} label="Border style" style={{ marginBottom: 0 }}>
                      <Select
                        style={{ width: 160 }}
                        options={[
                          { label: 'None', value: 'none' },
                          { label: 'Solid', value: 'solid' },
                          { label: 'Dashed', value: 'dashed' },
                          { label: 'Dotted', value: 'dotted' },
                          { label: 'Double', value: 'double' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item label="Border color" style={{ marginBottom: 0 }}>
                      <Space wrap>
                        <Form.Item name={['defaultDeepNestedLineStyle', 'borderColor']} noStyle>
                          <Input placeholder="#d9d9d9" style={{ width: 140 }} />
                        </Form.Item>
                        <ColorPicker
                          value={typeof deepNestedLineBorderColor === 'string' && deepNestedLineBorderColor.trim() ? deepNestedLineBorderColor : undefined}
                          onChange={(c) => form.setFieldValue(['defaultDeepNestedLineStyle', 'borderColor'], c.toHexString())}
                          showText
                        />
                      </Space>
                    </Form.Item>
                  </Space>
                ),
              },
            ]}
          />
        ),
      },
    ];
  }, [
    form,
    headerTextColor,
    headerBgColor,
    headerBorderColor,
    lineTextColor,
    lineBgColor,
    lineBorderColor,
    nestedLineTextColor,
    nestedLineBgColor,
    nestedLineBorderColor,
    deepNestedLineTextColor,
    deepNestedLineBgColor,
    deepNestedLineBorderColor,
    linkTextColor,
    linkBgColor,
    linkBorderColor,
  ]);

  return (
    <>
      <Form.Item noStyle shouldUpdate>
        {() => {
          const headerStyle = form.getFieldValue('defaultHeaderStyle');
          const lineStyle = form.getFieldValue('defaultLineStyle');
          const nestedLineStyle = form.getFieldValue('defaultNestedLineStyle');
          const deepNestedLineStyle = form.getFieldValue('defaultDeepNestedLineStyle');
          const linkStyle = form.getFieldValue('defaultLinkStyle');

          return (
            <div style={{ marginBottom: 12 }}>
              <Typography.Text strong>{previewTitle || 'Preview (click pencil to edit)'}</Typography.Text>
              <div style={{ marginTop: 8 }}>
                <Card size="small" title={<span style={{ opacity: 0.9 }}>Sample body</span>}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, lineHeight: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={asPreviewCss(headerStyle)}>Header / Section</span>
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openSection(['headerAdvanced'])} />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={asPreviewCss(lineStyle)}>
                            This is a sample line with a <span style={asPreviewCss(linkStyle)}>link</span>.
                          </span>
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openSection(['lineAdvanced', 'linkAdvanced'])} />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={asPreviewCss(nestedLineStyle)}>Nested line (level 1)</span>
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openSection(['nestedLineAdvanced'])} />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 20 }}>
                          <span style={asPreviewCss(deepNestedLineStyle)}>Nested line (level 2+)</span>
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openSection(['deepNestedLineAdvanced'])} />
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          );
        }}
      </Form.Item>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Text strong>Advanced editor</Typography.Text>
        <Button
          size="small"
          onClick={() => {
            setAdvancedVisible((v) => !v);
            if (!advancedVisible) setAdvancedKeys(['headerAdvanced']);
          }}
        >
          {advancedVisible ? 'Hide' : 'Show'}
        </Button>
      </div>

      <div
        style={{
          display: advancedVisible ? 'block' : 'none',
        }}
      >
        <Collapse
          size="small"
          activeKey={advancedKeys}
          onChange={(keys) => setAdvancedKeys(Array.isArray(keys) ? keys : [String(keys)])}
          items={collapseItems as any}
        />
      </div>
    </>
  );
};

